import { Inject, Injectable } from '@angular/core';
import {
  dateTimeDateToExcelDate,
  getLocalizedBoolean,
  transformResolutionAggregation,
} from '../../functions/util';
import { QueryParams } from '../types/query-params';
import { ApiService } from './api.service';
import { DateTime } from 'luxon';
import { catchError } from 'rxjs';

/* global console, process, Excel, localStorage */

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  constructor(@Inject(ApiService) private apiService: ApiService) {}

  async updateFunctions() {
    await Excel.run(async (context) => {
      let range = context.workbook.worksheets
        .getActiveWorksheet()
        .getUsedRange()
        .getSpecialCellsOrNullObject(Excel.SpecialCellType.formulas);
      range.load(['address']);
      await context.sync();
      if (!range.isNullObject) {
        range.calculate();
      }
      await context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  runHistoric(
    keys: string[],
    queryParams: QueryParams,
    diagram_format: string,
    queryStart?: DateTime,
    queryEnd?: DateTime
  ) {
    Excel.run((context) => {
      let range = context.workbook.getSelectedRange().getCell(0, 0);
      range.formulasLocal = [
        [
          `=${
            process.env['APPSETTING_NAMESPACE']
          }.GETHISTORICPRICES({"${keys.join('";"')}"};"${
            queryParams.currency
          }";"${queryParams.unit}";"${transformResolutionAggregation(
            queryParams.resolution
          )}";"${transformResolutionAggregation(queryParams.aggregation)}";"${
            queryParams.aggregation_scope
          }";"${queryParams.aggregation_method}";${
            queryStart ? dateTimeDateToExcelDate(queryStart) : ''
          };${
            queryEnd && !queryParams.auto_update
              ? dateTimeDateToExcelDate(queryEnd)
              : ''
          };${getLocalizedBoolean(
            queryParams.latest_price
          )};"${diagram_format}";${getLocalizedBoolean(
            queryParams.auto_update
          )};"${queryParams.nv_default}";"${
            queryParams.timezone !== undefined &&
            queryParams.timezone !== null &&
            queryParams.timezone !== ''
              ? queryParams.timezone
              : ''
          }"${queryParams.start_of_day !== undefined && queryParams.start_of_day !== null ? ';' + queryParams.start_of_day : ''})`,
        ],
      ];
      const lsJumpToNextCell = localStorage.getItem('feeder_jump_to_next_cell');
      if (lsJumpToNextCell !== null && JSON.parse(lsJumpToNextCell)) {
        let newRange = context.workbook
          .getSelectedRange()
          .getResizedRange(1, keys.length + 1)
          .getLastColumn()
          .getCell(0, 0);
        newRange.select();
      }
      return context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  runLive(
    market_area_id: any,
    price_type: any,
    exchange: any,
    product: any,
    headers: any[],
    unit: string,
    currency: string
  ) {
    Excel.run((context) => {
      let range = context.workbook.getSelectedRange().getCell(0, 0);
      range.formulasLocal = [
        [
          `=${
            process.env['APPSETTING_NAMESPACE']
          }.GETLIVEPRICES("${market_area_id}";"${price_type}";"${exchange}";"${product}";"${headers.join(
            ','
          )}";"${unit}";"${currency}";60)`,
        ],
      ];
      range.format.autofitColumns();
      return context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  runLiveByIds(ids: any[], headers: any[], unit: string, currency: string) {
    Excel.run((context) => {
      let range = context.workbook.getSelectedRange().getCell(0, 0);
      range.formulasLocal = [
        [
          `=${
            process.env['APPSETTING_NAMESPACE']
          }.GETLIVEPRICESBYIDS("${ids.join(',')}";60;"${headers.join(
            ','
          )}";"${unit}";"${currency}")`,
        ],
      ];
      range.format.autofitColumns();
      return context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  runFundamental(
    keys: any[],
    queryStart?: DateTime,
    queryEnd?: DateTime,
    detailed: boolean = false,
    queryParams: QueryParams = new QueryParams(),
    diagram_format: string = 'default'
  ) {
    Excel.run(async (context) => {
      let range = context.workbook.getSelectedRange().getCell(0, 0);
      range.load(['address']);
      range.formulasLocal = [
        [
          `=${
            process.env['APPSETTING_NAMESPACE']
          }.GETFUNDAMENTALDATA({"${keys.join('";"')}"};"${queryParams.unit}";` +
            `${queryStart ? +dateTimeDateToExcelDate(queryStart) : ''};${
              queryEnd ? +dateTimeDateToExcelDate(queryEnd) : ''
            };"${queryParams.resolution}";"${queryParams.aggregation}";"${queryParams.aggregation_scope}";"${queryParams.aggregation_method}";${getLocalizedBoolean(
              queryParams.latest_price
            )};${getLocalizedBoolean(
              queryParams.auto_update
            )};${getLocalizedBoolean(detailed)};"${queryParams.nv_default}";"${
              queryParams.timezone
            }";"${diagram_format}"${queryParams.start_of_day !== undefined && queryParams.start_of_day !== null ? ';' + queryParams.start_of_day : ''})`,
        ],
      ];
      range.format.autofitColumns();
      return context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  runForwardCurve(
    keys: string[],
    queryParams: QueryParams,
    diagram_format: string,
    queryStart: DateTime,
    queryEnd: DateTime
  ) {
    if (queryParams.max_version !== '') {
      this.runVersioned(
        keys,
        queryParams,
        diagram_format,
        queryStart,
        queryEnd
      );
    } else {
      Excel.run((context) => {
        let range = context.workbook.getSelectedRange().getCell(0, 0);
        range.formulasLocal = [
          [
            `=${
              process.env['APPSETTING_NAMESPACE']
            }.GETFORWARDCURVE({"${keys.join('";"')}"};"${
              queryParams.currency
            }";"${queryParams.unit}";"${transformResolutionAggregation(
              queryParams.resolution
            )}";"${transformResolutionAggregation(queryParams.aggregation)}";"${
              queryParams.aggregation_scope
            }";"${queryParams.aggregation_method}";${
              queryStart ? +dateTimeDateToExcelDate(queryStart) : ''
            };${
              queryEnd ? +dateTimeDateToExcelDate(queryEnd) : ''
            };"${diagram_format}";${getLocalizedBoolean(
              queryParams.auto_update
            )};"${queryParams.nv_default}";"${
              queryParams.timezone !== undefined &&
              queryParams.timezone !== null &&
              queryParams.timezone !== ''
                ? queryParams.timezone
                : ''
            }"${queryParams.start_of_day !== undefined && queryParams.start_of_day !== null ? ';' + queryParams.start_of_day : ''})`,
          ],
        ];
        const lsJumpToNextCell = localStorage.getItem(
          'feeder_jump_to_next_cell'
        );
        if (lsJumpToNextCell !== null && JSON.parse(lsJumpToNextCell)) {
          let newRange = context.workbook
            .getSelectedRange()
            .getResizedRange(1, keys.length + 1)
            .getLastColumn()
            .getCell(0, 0);
          newRange.select();
        }
        return context.sync();
      }).catch((error) => {
        throw new Error(error);
      });
    }
  }

  runVersioned(
    keys: string[],
    queryParams: QueryParams,
    diagram_format: string,
    queryStart: DateTime,
    queryEnd: DateTime
  ) {
    Excel.run((context) => {
      let range = context.workbook.getSelectedRange().getCell(0, 0);
      range.formulasLocal = [
        [
          `=${
            process.env['APPSETTING_NAMESPACE']
          }.GETVERSIONEDPRICES({"${keys.join('";"')}"};"${
            queryParams.currency
          }";"${queryParams.unit}";"${transformResolutionAggregation(
            queryParams.resolution
          )}";"${transformResolutionAggregation(queryParams.aggregation)}";"${
            queryParams.aggregation_scope
          }";"${queryParams.aggregation_method}";${
            queryStart ? +dateTimeDateToExcelDate(queryStart) : ''
          };${
            queryEnd ? +dateTimeDateToExcelDate(queryEnd) : ''
          };"${diagram_format}";${getLocalizedBoolean(
            queryParams.auto_update
          )};"${queryParams.nv_default}";"${
            queryParams.timezone !== undefined &&
            queryParams.timezone !== null &&
            queryParams.timezone !== ''
              ? queryParams.timezone
              : ''
          }";${
            typeof queryParams.max_version === 'string'
              ? `"${queryParams.max_version}"`
              : queryParams.max_version
          }${queryParams.start_of_day !== undefined && queryParams.start_of_day !== null ? ';' + queryParams.start_of_day : ''}
            )`,
        ],
      ];
      const lsJumpToNextCell = localStorage.getItem('feeder_jump_to_next_cell');
      if (lsJumpToNextCell !== null && JSON.parse(lsJumpToNextCell)) {
        let newRange = context.workbook
          .getSelectedRange()
          .getResizedRange(1, keys.length + 1)
          .getLastColumn()
          .getCell(0, 0);
        newRange.select();
      }
      return context.sync();
    }).catch((error) => {
      throw new Error(error);
    });
  }

  async generateTimeSeries(
    begin: string,
    end: string,
    resolution: string,
    country_code: string,
    show_peak: boolean,
    show_hours: boolean,
    show_holiday: boolean,
    timezone: string,
    date_format: string
  ) {
    this.apiService
      .generateTimeSeries(
        begin,
        end,
        resolution,
        show_peak || show_holiday || show_hours,
        country_code,
        timezone,
        date_format
      )
      .pipe(
        catchError((error) => {
          console.error(error);
          return [
            [error.message + ' ' + error.status + ' ' + error.statusText],
          ];
        })
      )
      .subscribe((response) => {
        let value_count = response.values.length;
        let offset_additional_info = date_format === 'Local & UTC' ? 1 : 0;
        for (let i = 0; i < value_count; i++) {
          if (date_format === 'Local & UTC') {
            response.values[i].splice(1, 1);
            response.values[i].splice(2, 1);
            response.values[i][1] = `=VALUE("${
              response.values[i][1]
                .replace('T', ' ')
                .replace('Z', '')
                .split('+')[0]
            }")`;
          } else {
            response.values[i].splice(1, 1);
          }
          if (show_peak || show_holiday || show_hours) {
            if (!show_holiday) {
              response.values[i].splice(4 + offset_additional_info, 1);
            }
            if (!show_hours) {
              response.values[i].splice(3 + offset_additional_info, 1);
            }
            if (!show_peak) {
              response.values[i].splice(2 + offset_additional_info, 1);
            }
            if (show_peak && show_hours) {
              let tmp = response.values[i][2 + offset_additional_info];
              response.values[i][2 + offset_additional_info] =
                response.values[i][3 + offset_additional_info];
              response.values[i][3 + offset_additional_info] = tmp;
            }
          }
          response.values[i][0] = `=VALUE("${response.values[i][0]
            .replace('T', ' ')
            .split('+')[0]
            .replace('Z', '')}")`;
          if (date_format === 'Local & UTC') {
            response.values[i].splice(2, 1);
          } else {
            response.values[i].splice(1, 1);
          }
        }
        let headers = ['Date/Time'];
        if (show_hours) {
          headers.push('Hours');
        }
        if (show_peak) {
          headers.push('Peak');
        }
        if (show_holiday) {
          headers.push('Holiday');
        }
        if (date_format === 'Local & UTC') {
          headers.unshift('UTC Date/Time');
        } else if (date_format === 'UTC') {
          headers[0] += ' UTC';
        } else {
          headers[0] += ' Local';
        }
        response.values.unshift(headers);
        let numberFormat = this.getNumberFormat(resolution);
        return Excel.run(async (context) => {
          let range = context.workbook
            .getSelectedRange()
            .getCell(0, 0)
            .getResizedRange(
              response.values.length - 1,
              response.values[0].length - 1
            );
          range.values = response.values;
          range.getColumn(0).numberFormat = [[numberFormat]];
          if (date_format === 'Local & UTC') {
            range.getColumn(1).numberFormat = [[numberFormat]];
          }
          await context.sync();
          range.format.autofitColumns();
          range.copyFrom(range, Excel.RangeCopyType.values);
          await context.sync();
        });
      });
  }

  getNumberFormat(resolution: string) {
    switch (resolution) {
      case '15min':
        return 'dd.mm.yyyy hh:MM';
      case '30min':
        return 'dd.mm.yyyy hh:MM';
      case '1H':
      case '1h':
        return 'dd.mm.yyyy hh:MM';
      case '1D':
        return 'dd.mm.yyyy';
      case '1M':
        return 'mm.yyyy';
      case '1QS':
        return 'mm.yyyy';
      case '1Y':
        return 'yyyy';
      default:
        return 'dd.mm.yyyy';
    }
  }
}
